<template>
  <div class="row">
    <div class="col-md-12 m-b-2">
      <el-table :data="shipping" :row-class-name="highlightChanges" border>
        <el-table-column prop="label"
                         label="Shipping Address"
                         class-name="bold"
                         :min-width="10"/>
        <el-table-column prop="original"
                         label="Original"
                         header-align="center"
                         :min-width="30"/>
        <el-table-column v-if="updatedOrder"
                         prop="updated"
                         label="Updated"
                         header-align="center"
                         :min-width="30"/>
      </el-table>
    </div>
    <div class="col-md-12 m-b-2">
      <el-table :data="lineItems" :row-class-name="areProductsEqual" border>
        <el-table-column prop="label"
                         label="Line Items"
                         class-name="bold"
                         :min-width="10"/>
        <el-table-column prop="original"
                         label="Original"
                         header-align="center"
                         :min-width="30">
          <template slot-scope="{row: {original}}">
            <template v-if="original && Object.keys(original).length > 0">
              <div>{{original.title}}</div>
              <div>{{original.sku}}</div>
              <div>Qty: {{original.quantity}}</div>
              <price-display
                  :originalPrice="Number(original.price)"
                  :updatePrice="calculatePriceFromPretaxPrice(original)"
              />
            </template>
          </template>
        </el-table-column>
        <el-table-column v-if="updatedOrder"
                         prop="updated"
                         label="Updated"
                         header-align="center"
                         :min-width="30">
          <template slot-scope="{row: {original, updated}}">
            <template v-if="updated && Object.keys(updated).length > 0">
              <div>{{updated.title}}</div>
              <div>{{updated.sku}}</div>
              <div>Qty: {{updated.quantity}}</div>
              <price-display
                  :originalPrice="original.price ? Number(original.price) : 0"
                  :updatePrice="calculatePrice(updated)"
              />
            </template>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div class="col-md-12 m-b-2">
      <el-table :data="payment" :row-class-name="arePaymentLinesEqual" border>
        <el-table-column prop="label"
                         label="Payment Authorized"
                         class-name="bold"
                         :min-width="10"/>
        <el-table-column prop="original"
                         label="Original"
                         header-align="center"
                         :min-width="30">
          <template slot-scope="{row: {original, label}}">
            <template v-if="label === 'Subtotal'">
              <price-display
                  :originalPrice="Number(original.total_line_items_price)"
                  :updatePrice="Number(original.subtotal_price)"
              />
            </template>
            <template v-else-if="typeof original === 'string'">
              {{original}}
            </template>
            <template v-else-if="typeof original === 'object'">
              <span>{{original.price | currency}}</span> -
              <small>{{original.rate | percent}}</small>
              <small>{{original.title}}</small>
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="updated"
                         label="Updated"
                         header-align="center"
                         :min-width="30">
          <template slot-scope="{row: {original, updated, label}}">
            <template v-if="label === 'Subtotal'">
              <price-display
                  :originalPrice="Number(original.subtotal_price)"
                  :updatePrice="Number(updated.subtotal_price)"
              />
            </template>
            <template v-else-if="typeof updated === 'string'">
              {{updated}}
            </template>
            <template v-else-if="typeof updated === 'object'">
              <span>{{updated.price | currency}}</span> -
              <small>{{updated.rate | percent}}</small>
              <small>{{updated.title}}</small>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
  import collect from 'collect.js';
  import Decimal from 'decimal.js';
  import {currency, percent} from '../../../filters';
  import {
    rollUpTaxLines,
    extractD365Item,
    extractProperty,
  } from '../../../utils';
  import PriceDisplay from '../Shared/PriceDisplay';
  import {SHOPIFY_ADDRESS_FIELDS} from '../../../lang/en/constants';

  export default {
    name: 'OrderDifference',
    props: {
      originalOrder: {
        required: true,
        type: Object,
      },
      updatedOrder: {
        required: false,
        type: Object,
        default: null,
      },
      d365Order: {
        required: true,
        type: Object,
      },
      discountPercent: {
        required: false,
        type: Number,
        default: 0,
      },
    },
    components: {
      PriceDisplay,
    },
    filters: {
      currency,
      percent,
    },
    computed: {
      orderTaxLines() {
        return rollUpTaxLines(this.originalOrder.tax_lines);
      },
      updatedOrderTaxLines() {
        return rollUpTaxLines(this.updatedOrder.tax_lines);
      },
      lineItems() {
        const lineItemsOriginal = collect(_.cloneDeep(this.originalOrder.line_items));
        let lineItemsUpdated = collect(_.cloneDeep(this.updatedOrder.line_items));
        let lineItems = [];
        let key = 0;

        // Iterate over the order with the most items
        while(lineItemsOriginal.isNotEmpty() || lineItemsUpdated.isNotEmpty()){
          let original = {};
          let updated = {};

          // If original has more or same amount of items,
          // we will try to match to match updated items
          if (lineItemsOriginal.isNotEmpty()) {
            original = lineItemsOriginal.shift();
          }

          // If updated has  more items
          if (lineItemsUpdated.isNotEmpty()) {
            if (original) {
              updated = lineItemsUpdated.firstWhere('product_id', original.product_id) || lineItemsUpdated.firstWhere('sku', original.sku) || lineItemsUpdated.first();
            } else {
              updated = lineItemsUpdated.first();
            }

            lineItemsUpdated = lineItemsUpdated.filter((value) => value !== updated);
          }

          lineItems.push({
            label: ++key,
            original,
            updated,
          });
        }

        return lineItems;
      },
      payment() {
        const taxLines = [];
        const taxLinesMax = Math.max(
            this.orderTaxLines.length,
            this.updatedOrderTaxLines.length,
        );

        this.sortByTitle(this.orderTaxLines);
        this.sortByTitle(this.updatedOrderTaxLines);

        for (let key = 0; key < taxLinesMax; key++) {
          let original = '';
          let updated = '';

          if (this.orderTaxLines[key]) {
            original = {
              price: this.orderTaxLines[key].price,
              rate: this.orderTaxLines[key].rate,
              title: this.orderTaxLines[key].title,
            };
          }

          if (this.updatedOrderTaxLines[key]) {
            updated = {
              price: this.updatedOrderTaxLines[key].price,
              rate: this.updatedOrderTaxLines[key].rate,
              title: this.updatedOrderTaxLines[key].title,
            };
          }

          taxLines.push({
            label: `Tax Line ${key + 1}`,
            original,
            updated,
          });
        }

        const originalShippingPrice = this.originalOrder.shipping_lines.length >
        0 ? currency(this.originalOrder.shipping_lines[0].discounted_price) : 0;
        const originalShippingTitle = this.originalOrder.shipping_lines.length >
        0 ? this.originalOrder.shipping_lines[0].title : 'No Shipping';
        //
        const updatedShippingPrice = this.updatedOrder ?
            currency(this.updatedOrder.shipping_line ? this.updatedOrder.shipping_line.price || 0 : 0) : '';
        const updatedShippingTitle = this.updatedOrder
            ? (this.updatedOrder.shipping_line ? this.updatedOrder.shipping_line.title : 'No shipping')
            : '';

        return [
          {
            label: 'Subtotal',
            original: this.originalOrder,
            updated: this.updatedOrder,
          },
          {
            label: 'Shipping',
            original: `${originalShippingPrice} - ${originalShippingTitle}`,
            updated: `${updatedShippingPrice} - ${updatedShippingTitle}`,
          },
          taxLines,
          {
            label: 'Total Tax',
            original: currency(this.originalOrder.total_tax),
            updated: this.updatedOrder
                ? currency(this.updatedOrder.total_tax)
                : 0,
          },
          {
            label: 'Adjustments',
            original: -currency(this.originalOrder.adjustments.reduce((carry, current) => carry + current.amount, 0)) || '',
            updated: ''
          },
          {
            label: 'Total',
            original: currency(this.originalOrder.total_price),
            updated: this.updatedOrder
                ? currency(this.updatedOrder.total_price)
                : 0,
          },
        ].flat();
      },
      shipping() {
        const {shipping_address: shopifyShippingAddress} = this.originalOrder;
        const {shipping_address: refundShippingAddress} = this.updatedOrder;
        return Object.entries(SHOPIFY_ADDRESS_FIELDS).map(([key, value]) => ({
          label: value,
          original: this.extractProperty(shopifyShippingAddress, key),
          updated: this.extractProperty(refundShippingAddress, key),
        }));
      },
    },
    methods: {
      areProductsEqual({row: {original: originalLineItem, updated: updatedLineItem}}) {
        if (originalLineItem && updatedLineItem) {
          const title = originalLineItem.title !== updatedLineItem.title;
          const sku = originalLineItem.sku !== updatedLineItem.sku;
          const quantity = originalLineItem.quantity !== updatedLineItem.quantity;
          const price = Number(originalLineItem) !== Number(updatedLineItem);

          if (title || sku || quantity || price ||
            updatedLineItem.applied_discount) {
            return 'highlight';
          }
        }
        return '';
      },
      arePaymentLinesEqual({row: {original: originalLineItem, updated: updatedLineItem}}) {
        if (originalLineItem.price && updatedLineItem.price) {
          const price = Number(originalLineItem.price) !== Number(updatedLineItem.price);
          const rate = Number(originalLineItem.rate) !== Number(updatedLineItem.rate);
          const title = originalLineItem.title !== updatedLineItem.title;
          return price || rate || title ? 'highlight' : '';
        }

        if (originalLineItem !== updatedLineItem) {
          return 'highlight';
        }

        return '';
      },
      highlightChanges({row}) {
        const rowClasses = [];
        const {original, updated} = row;

        if (!original && !updated) {
          rowClasses.push('hidden');
        }

        if (original !== updated) {
          rowClasses.push('highlight');
        }

        return rowClasses;
      },
      sortByTitle(taxLines) {
        taxLines.sort(function(a, b) {
          return ('' + a.title).localeCompare(b.title);
        });
      },
      extractD365Item,
      extractProperty,
      calculatePriceFromPretaxPrice({pre_tax_price, price, quantity}) {
        return (new Decimal(pre_tax_price || price)).div(quantity).toNumber();
      },
      calculatePrice({applied_discount, price}) {
        let newPrice = new Decimal(price);
        if (applied_discount) {
          newPrice = newPrice.minus(applied_discount.amount);
        }
        else if(this.discountPercent) {
          newPrice = newPrice.mul(1 - Math.abs(this.discountPercent));
        }
        return Math.round(newPrice.toNumber()*100)/100;
      },
    },
  };
</script>

<style>
  .el-table .highlight {
    background-color: #dff0d8;
  }

  .el-table .bold {
    font-weight: bold;
  }
</style>
